import React, { useCallback, useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Video, FileText, Trash2, Loader2, UploadCloud, Plus } from "lucide-react";
import { toast } from "sonner";
import { mlMediaList, mlMediaCreate, mlMediaDelete, mlUpload, fileUrl } from "../../lib/api";
import { C } from "./mlShared";

const DIRS = [
  { key: "image", label: "Images", icon: ImageIcon },
  { key: "video", label: "Videos", icon: Video },
  { key: "text", label: "Text", icon: FileText },
];
const inputCls = "w-full rounded-md border px-3 py-2 text-sm outline-none";
const inputStyle = { borderColor: C.line, background: "#fff", color: C.ink };

export const MediaLibrary = () => {
  const [dir, setDir] = useState("image");
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const d = await mlMediaList(dir); setItems(d.items || []); }
    catch { toast.error("Could not load library"); }
  }, [dir]);
  useEffect(() => { load(); setTitle(""); setTags(""); setUrl(""); setText(""); }, [load]);

  const tagList = () => tags.split(",").map((s) => s.trim()).filter(Boolean);

  const pickFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { const up = await mlUpload(file); setUrl(up.url); if (!title) setTitle(file.name.replace(/\.[^.]+$/, "")); toast.success("Uploaded — add a title & save"); }
    catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const add = async () => {
    if (!title.trim()) { toast.error("Add a title"); return; }
    if (dir === "image" && !url) { toast.error("Upload an image first"); return; }
    if (dir === "video" && !url.trim()) { toast.error("Paste a video URL"); return; }
    if (dir === "text" && !text.trim()) { toast.error("Add some text"); return; }
    setBusy(true);
    try {
      await mlMediaCreate({ kind: dir, title: title.trim(), tags: tagList(), url, text });
      setTitle(""); setTags(""); setUrl(""); setText("");
      toast.success("Saved to library");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await mlMediaDelete(id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="ml-media-library">
      {/* Directory tabs */}
      <div className="mb-4 flex gap-2">
        {DIRS.map((d) => (
          <button key={d.key} onClick={() => setDir(d.key)} data-testid={`ml-media-dir-${d.key}`}
            className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold"
            style={{ background: dir === d.key ? C.navy : "#fff", color: dir === d.key ? "#fff" : C.navy, border: `1px solid ${C.line}` }}>
            <d.icon className="h-4 w-4" /> {d.label}
          </button>
        ))}
      </div>

      {/* Add form */}
      <div className="mb-6 rounded-xl border p-4" style={{ borderColor: C.line, background: C.card }}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className={inputCls} style={inputStyle} data-testid="ml-media-title" />
          <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags (comma separated)" className={inputCls} style={inputStyle} data-testid="ml-media-tags" />
        </div>
        <div className="mt-3">
          {dir === "image" && (
            <div className="flex items-center gap-3">
              {url ? <img src={fileUrl(url)} alt="" className="h-16 w-24 rounded object-cover" /> : <div className="flex h-16 w-24 items-center justify-center rounded" style={{ background: C.line }}><ImageIcon className="h-5 w-5" style={{ color: C.muted }} /></div>}
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm" style={{ borderColor: C.line, color: C.navy }} data-testid="ml-media-upload">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload image
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickFile} />
            </div>
          )}
          {dir === "video" && (
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Video URL (YouTube / MP4 link)" className={inputCls} style={inputStyle} data-testid="ml-media-video-url" />
          )}
          {dir === "text" && (
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Text snippet / note" className={inputCls} style={inputStyle} data-testid="ml-media-text" />
          )}
        </div>
        <button onClick={add} disabled={busy} data-testid="ml-media-add"
          className="mt-3 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: C.rust }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to {DIRS.find((d) => d.key === dir).label}
        </button>
      </div>

      {/* Grid */}
      {items.length === 0 ? (
        <p className="text-sm" style={{ color: C.muted }} data-testid="ml-media-empty">No items in this library yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((it) => (
            <div key={it.id} className="overflow-hidden rounded-xl border" style={{ borderColor: C.line, background: C.card }} data-testid={`ml-media-item-${it.id}`}>
              {it.kind === "image" && <img src={fileUrl(it.url)} alt={it.title} className="h-32 w-full object-cover" />}
              {it.kind === "video" && <div className="flex h-32 w-full items-center justify-center" style={{ background: "#0e1a24" }}><Video className="h-8 w-8 text-white/70" /></div>}
              {it.kind === "text" && <div className="h-32 w-full overflow-hidden p-3 text-xs" style={{ background: "#fff", color: C.muted }}>{it.text}</div>}
              <div className="p-2.5">
                <div className="truncate text-sm font-semibold" style={{ color: C.navy }}>{it.title}</div>
                {it.tags?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {it.tags.slice(0, 4).map((t, i) => <span key={i} className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: C.line, color: C.muted }}>{t}</span>)}
                  </div>
                )}
                <button onClick={() => del(it.id)} className="mt-2 inline-flex items-center gap-1 text-xs" style={{ color: "#b23b3b" }} data-testid={`ml-media-del-${it.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
